/**
 * Native-webview message bridge: send `login`, `closePanel`, `navigateTo`
 * actions to the iOS/Android shell, and subscribe to inbound SSO login events.
 *
 * Tiny dispatcher. iOS receives messages via `webkit.messageHandlers`, Android
 * via injected `Android.[action]` methods; `sendMessageToContainer` picks the
 * right channel from the window shape and silently no-ops on web. The
 * `NativeWindow` type is the contract a native shell must inject — keep these
 * action names in sync with the host app's handlers.
 *
 * `subscribeToContainerLogin` is the single inbound hook for container-injected
 * SSO tokens. The `requestSignUp` flag in its callback is set to `true` when
 * the token arrives in response to an explicit `requestAppLogin` call —
 * distinguishing user-initiated sign-in/sign-up from the implicit on-load flow.
 * New inbound channels (e.g., iframe postMessage) can be wired here without
 * changing the consumer.
 */

import { getIsInAndroid, getIsInIOS } from './util';

let signUpOnNextLogin = false;

export const requestAppLogin = () => {
    signUpOnNextLogin = true;
    sendMessageToContainer('login');
};

export const requestAppClosePanel = () => {
    sendMessageToContainer('closePanel');
};

export const requestAppNavigateToExternalUrl = (url: any) => {
    sendMessageToContainer('navigateTo', url);
};

export type NativeWindow = {
    parent: any;
    webkit?: {
        messageHandlers?: Record<string, any>;
    };
    Android?: any;
};

export type ContainerLoginEvent = { ssoToken: string; provider: string; requestSignUp: boolean };

export function subscribeToContainerLogin(callback: (event: ContainerLoginEvent) => void): () => void {
    const handler = (event: any) => {
        const { ssoToken, provider } = event?.detail ?? {};
        const requestSignUp = event?.detail?.requestSignUp ?? signUpOnNextLogin;
        signUpOnNextLogin = false;
        callback({ ssoToken, provider, requestSignUp });
    };
    document.addEventListener('nativeMessageEvent', handler);
    return () => document.removeEventListener('nativeMessageEvent', handler);
}

export function sendMessageToContainer(action: string, params = undefined as any) {
    const nativeWindow = window as NativeWindow;
    if (getIsInIOS(nativeWindow)) {
        nativeWindow.webkit?.messageHandlers?.[action].postMessage(params);
    } else if (getIsInAndroid(nativeWindow)) {
        nativeWindow.Android?.[action](params);
    }
}
